import { T, useTranslate } from '@tolgee/react';
import { notification, Tooltip } from 'antd';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { CheckSvg, CrossSvg, QuestionMarkSvg } from '../../../assets/icons';
import {
  AIFeedbackFilledSvg,
  AnswersFilledSvg,
  DrawingFilledSvg,
  PollFilledSvg,
  WordCloudFilledSvg,
} from '../../../assets/icons/ActivityIcons';
import GridSvg from '../../../assets/icons/GridSvg';
import PodiumSvg from '../../../assets/icons/PodiumSvg';
import VotingSvg from '../../../assets/icons/VotingSvg';
import theme from '../../../assets/theme';
import useGame from '../../../hooks/useGame';
import useGameSaveSocket from '../../../hooks/useGameSaveSocket';
import { useMetrics } from '../../../hooks/useMetrics';
import { useActivityForSlideType } from '../../../utils/creator';
import { createInteractiveSlideByType } from '../../../utils/gameUtil';
import { notUndefined } from '../../../utils/general';
import {
  generateRoundOptionColor,
  generateUniqueId,
  hasVoting,
  RoundState,
} from '../../../utils/roundUtils';
import { MAX_POLL_OPTIONS } from '../../../utils/templateUtils';
import { Input } from '../../_atoms/_Input';
import { Switch } from '../../_atoms/_Switch';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { isCuripodText, parseStringToSlateObject } from '../../SlideComponents/slateUtil';
import { PollOptions, Slide, SlideType } from '../../SlideComponents/slide.types';
import {
  insertSlidesAfterSelectedSlide,
  retrieveSlideQuestionElementSlate,
} from '../../SlideComponents/slideUtil';
import { BetaFlag } from '../RoundTypeSelector';
import {
  ButtonIconWrapper,
  QuestionButtonsType,
  TypeButton,
  useRoundTypeButtons,
} from '../RoundTypeSelector/RoundTypeSelector';

export const roundTypeToIcon = (type: SlideType) => {
  if (type === 'answers') return <AnswersFilledSvg />;
  if (type === 'aiAssessedAnswers') return <AIFeedbackFilledSvg />;
  if (type === 'podium') return <PodiumSvg />;
  if (type === 'canvas') return <DrawingFilledSvg />;
  if (type === 'responsegrid') return <GridSvg />;
  if (type === 'wordcloud') return <WordCloudFilledSvg />;
  if (type === 'poll') return <PollFilledSvg />;
  if (type === 'voting') return <VotingSvg />;
  return null;
};

const initPollOptions: PollOptions[] = [
  { id: '', displayName: '', color: theme.colors.orange },
  { id: '', displayName: '', color: theme.colors.green },
];
const AddNewActivity = ({
  gameId,
  setShow,
  roundState,
  currentSlide,
}: {
  gameId: string;
  roundState?: RoundState;
  setShow: (show: boolean) => void;
  currentSlide?: Slide;
}) => {
  const metrics = useMetrics();
  const { t } = useTranslate();
  const { autoSave } = useGameSaveSocket();
  const { game } = useGame(gameId);
  const [selectedAcivity, setSelectedActivity] = useState<SlideType>('answers');
  const [newActivityTitle, setNewActivityTitle] = useState<string>('');
  const [votingIsSelected, setVotingIsSelected] = useState(false);
  const [pollOptions, setPollOptions] = useState<PollOptions[]>(
    initPollOptions.map(o => ({ ...o, id: generateUniqueId().toString() })),
  );

  let slateTitleColor: string = theme.colors.black;
  if (currentSlide) {
    const slate = parseStringToSlateObject(
      retrieveSlideQuestionElementSlate(currentSlide),
    );
    const firstElement = slate?.find(element => element.type === 'paragraph');
    const firstChild = firstElement?.children?.[0];
    // Using the leaf node color if it exists - otherwise use the paragraph color.
    slateTitleColor =
      (firstChild
        ? isCuripodText(firstChild)
          ? firstChild.color
          : firstElement?.color
        : firstElement?.color) || theme.colors.black;
  }
  const { response: roundTypeButtons } = useRoundTypeButtons();
  const activity = useActivityForSlideType(selectedAcivity);
  const clearInputs = () => {
    setSelectedActivity('answers');
    setNewActivityTitle('');
    setVotingIsSelected(false);
    setPollOptions(
      initPollOptions.map(o => ({ ...o, id: generateUniqueId().toString() })),
    );
  };

  const createActivity = () => {
    if (!game) return undefined;
    if (!game.currentRound) return undefined;
    const currentBackground =
      currentSlide && currentSlide.backgroundMedia ? currentSlide.backgroundMedia : null;
    const newSlides = createInteractiveSlideByType({
      type: selectedAcivity,
      hasVoting: votingIsSelected,
      title: newActivityTitle ? newActivityTitle : null,
      votingTitle: votingIsSelected ? t('Most relevant') : null,
      roundOptions: pollOptions,
      backgroundMedia: currentBackground,
      wasAddedDuringLive: true,
      metadata: { grade: game.metadata?.grade || '5th' },
      textColor: slateTitleColor,
    });

    return newSlides;
  };

  const handleAddActivity = () => {
    setShow(false);
    clearInputs();
    metrics.logEvent('AddNewActivity.AddActivity.Clicked', {
      selectedAcivity,
      hasVoting,
    });
    const newActivity = createActivity();

    const updatedSlides = insertSlidesAfterSelectedSlide(
      game?.slides || [],
      newActivity || [],
      currentSlide?.id,
    );

    if (game) {
      // if the activity can be skiped, the code automatic sets the user to that activity
      autoSave({
        game: {
          ...game,
          slides: updatedSlides,
          currentRound:
            (roundState === 'pre' || roundState === 'post') && newActivity
              ? newActivity[0].id
              : game.currentRound,
          roundStartsAt:
            roundState === 'pre' || roundState === 'post' ? null : game.roundStartsAt,
        },
        onSuccessFunction: _ => {
          if (roundState !== 'pre' && roundState !== 'post') {
            notification.success({
              message: t('New activity added'),
              top: 90,
              duration: 2,
              style: {
                fontWeight: 'bold',
              },
              icon: (
                <IconWrapper
                  color={theme.colors.green}
                  height="40"
                  width="40"
                  style={{ display: 'flex', alignItems: 'center', height: '20px' }}
                >
                  {' '}
                  <CheckSvg />
                </IconWrapper>
              ),
            });
          }
        },
      });
    }
  };

  const handleAddPollOption = () => {
    if (pollOptions.length < MAX_POLL_OPTIONS) {
      const existingColors = pollOptions?.map(o => o.color).filter(notUndefined);
      generateRoundOptionColor(existingColors || []);
      const newOption: PollOptions = {
        id: generateUniqueId().toString(),
        displayName: '',
        color: generateRoundOptionColor(existingColors || []),
      };
      setPollOptions(e => [...e, newOption]);
    }
  };

  useEffect(() => {
    if (hasVoting(selectedAcivity)) {
      setVotingIsSelected(true);
    }
  }, [selectedAcivity]);
  if (!activity) return null;
  return (
    <AddNewActivityContainer>
      <span style={{ display: 'flex', gridColumn: '1/3' }}>
        <T keyName="Add new activity" defaultValue="Add new activity" />
        <Tooltip title={t('Add activity on the fly', 'Add activity on the fly')}>
          <IconWrapper height="20px" width="20px" style={{ marginLeft: '10px' }}>
            <QuestionMarkSvg />
          </IconWrapper>
        </Tooltip>
      </span>
      {roundTypeButtons.map((button: QuestionButtonsType) => {
        return (
          <TypeButton
            key={button.text}
            $active={selectedAcivity === button.type}
            $disabled={false}
            onClick={() => {
              if (button.type) setSelectedActivity(button.type as SlideType);
            }}
          >
            {button.beta && <BetaFlag />}
            <ButtonIconWrapper>{button.icon}</ButtonIconWrapper>
            <p>{button.text}</p>
          </TypeButton>
        );
      })}

      <div
        style={{
          gridColumn: '1/3',
          marginTop: '10px',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ width: '100%' }}>{t('Your question')}</span>
        {selectedAcivity !== 'aiAssessedAnswers' && (
          <span style={{ gridColumn: '1/3', opacity: 0.7 }}>
            <T
              keyName="Give oral instructions, or fill in the question"
              defaultValue="Give oral instructions, or fill inn"
            />
          </span>
        )}
      </div>

      <Input
        style={{ gridColumn: '1/3' }}
        maxLength={selectedAcivity !== 'aiAssessedAnswers' ? 100 : 250}
        onChange={e => setNewActivityTitle(e.target.value)}
        value={newActivityTitle}
      />

      {hasVoting(selectedAcivity) && (
        <span
          style={{
            opacity: votingIsSelected ? 1 : 0.6,
            marginTop: '10px',
            gridColumn: '1/3',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {t('Participants votes')}
          <Switch
            value={votingIsSelected}
            color={theme.colors.green}
            style={{ marginLeft: '10px', marginRight: '10px' }}
            onChange={() => setVotingIsSelected(!votingIsSelected)}
          />
          <Tooltip
            title={t(
              'toggle_voting',
              'Add voting to let participants vote and see the results on a podium. Or turn off voting to display the results in a grid',
            )}
          >
            <IconWrapper height="20px" width="20px">
              <QuestionMarkSvg />
            </IconWrapper>
          </Tooltip>
        </span>
      )}
      {selectedAcivity === 'poll' && (
        <div
          style={{
            gridColumn: '1/3',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
          }}
        >
          <p style={{ gridColumn: '1/3', marginBottom: '0', marginTop: '10px' }}>
            {t('Poll options')}
          </p>
          {pollOptions.map((o, index) => (
            <div
              style={{ position: 'relative', margin: '5px', marginLeft: 0 }}
              key={o.id}
            >
              <PollInputContainer id={o.id} $bottomColor={o.color}>
                <PollInput
                  $bottomColor={o.color}
                  maxLength={30}
                  borderLess={true}
                  value={o.displayName}
                  placeholder={`${t('Option')} ${index + 1}`}
                  onChange={e =>
                    setPollOptions(existing => {
                      return existing.map(option => {
                        if (option.id === o.id) {
                          return {
                            ...option,
                            displayName: e.target.value,
                          };
                        }
                        return option;
                      });
                    })
                  }
                />
              </PollInputContainer>
              {pollOptions.length > 2 && (
                <CrossIconPollOptionWrapper
                  onClick={() =>
                    setPollOptions(existing => {
                      return existing.filter(option => option.id !== o.id);
                    })
                  }
                >
                  <CrossSvg />
                </CrossIconPollOptionWrapper>
              )}
            </div>
          ))}

          {pollOptions.length < MAX_POLL_OPTIONS && (
            <div style={{ gridColumn: '1/3' }}>
              <Button
                onClick={handleAddPollOption}
                styles={{
                  opacity: 0.8,
                  border: 'solid black 1px',
                  margin: '5px',
                  padding: '10px 5px',
                  backgroundColor: theme.colors.white,
                  marginLeft: 0,
                  fontSize: '13px',
                  width: '47%',
                }}
                type="ghost"
              >
                + {t('addOption', 'Add option')}
              </Button>
            </div>
          )}
        </div>
      )}
      {activity && activity.alternateImage && (
        <ImageWrapper>
          {votingIsSelected ? (
            <Image width={123} height={100} src={activity?.image?.src ?? ''} />
          ) : (
            <Image width={123} height={100} src={activity?.alternateImage?.src} />
          )}
        </ImageWrapper>
      )}
      <Button
        type="primary"
        disabled={!selectedAcivity}
        onClick={handleAddActivity}
        styles={{ marginTop: '20px', gridColumn: '1/3' }}
      >
        <T keyName="Add activity" defaultValue="Add activity" />
      </Button>
    </AddNewActivityContainer>
  );
};

const CrossIconPollOptionWrapper = styled.div`
  color: ${theme.colors.black};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  background-color: black;
  color: white;
  align-items: center;
  justify-content: center;
  svg {
    height: 10px;
    width: 10px;
  }
  cursor: pointer;
  border-radius: 50px;
  padding: 5px;
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  top: -10px;
  right: -5px;
  position: absolute;
`;

const AddNewActivityContainer = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 20px;
  padding: 30px;
  border-radius: 10px;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
`;
const ImageWrapper = styled.div`
  flex: 1;
  margin-bottom: 10px;
  img {
    max-height: 100px;
    object-fit: fill;
  }
`;

const PollInput = styled(Input)<{ $bottomColor?: string }>`
  border: solid 1px transparent;
`;

export const ActionLabelContainer = styled.div<{ $bottomColor?: string }>`
  position: relative;
  width: 100%;
  max-height: 67px;
  background-color: ${theme.colors.white};
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  border-top-right-radius: 20px;
  border-top-left-radius: 20px;
  border-bottom: 5px solid
    ${({ $bottomColor }) => ($bottomColor ? $bottomColor : theme.colors.green)};
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  padding: 20px 40px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  h1 {
    width: 100%;
    font-size: 24px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    margin-bottom: 0;
    color: ${theme.colors.black};
    font-weight: bold;
  }
  @media (max-width: 600px) {
    h1 {
      font-size: 30px;
    }
  }
  @media (max-width: 400px) {
    h1 {
      font-size: 22px;
    }
  }
`;

const PollInputContainer = styled(ActionLabelContainer)<{ $bottomColor?: string }>`
  padding: 0;
  width: 100%;
`;
export default AddNewActivity;
